import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { extractTableFieldsFromColumns } from "../../../../lib/api/readQuery";
import {
  persistIncidentsWorkspaceSelection,
  resolveIncidentsWorkspaceRestore,
} from "../../../../lib/incident/workspaceSelection";
import authService from "../../../../services/authService";
import { customerService, type Customer } from "../../../../services/customerService";
import { incidentService } from "../../../../services/incidentService";
import { sourceSystemService } from "../../../../services/sourceSystemService";
import type { Incident } from "../../../../types/incident";
import type { IncidentFolderId } from "../../lib/constants";
import {
  countActiveFilters,
  defaultIncidentFilters,
  parseIncidentsPage,
  type IncidentFilters,
} from "../../lib/filterUtils";
import { buildIncidentListSearchFilter } from "../../../../lib/incident/searchFilters";
import IncidentModel from "../../model";

const INCIDENT_LIST_FIELDS = extractTableFieldsFromColumns(IncidentModel.cols, [
  "detectedAnomalyEvents",
  "description",
  "sourceSystemId",
  "sourceSystem",
  "customerId",
  "customer",
  "openSearchId",
  "comments",
  "tags",
  "subcategory",
  "fromMail",
]);

const EMPTY_FOLDER_COUNTS: Record<IncidentFolderId, number | null> = {
  inbox: null,
  group: null,
  all: null,
  anomalies: null,
  new: null,
  critical: null,
  resolved: null,
};

export function useIncidentsWorkspace() {
  const location = useLocation();
  const initialRestore = useMemo(
    () => resolveIncidentsWorkspaceRestore(location.state),
    // Restore only when the workspace mounts (e.g. returning from detail).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [folderCounts, setFolderCounts] = useState<Record<IncidentFolderId, number | null>>(
    EMPTY_FOLDER_COUNTS,
  );
  const [loading, setLoading] = useState(true);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [pendingRestoreId, setPendingRestoreId] = useState<string | null>(
    initialRestore?.incidentId ?? null,
  );
  const [selectedFolder, setSelectedFolder] = useState<IncidentFolderId>(
    initialRestore?.folder ?? "inbox",
  );
  const [filters, setFilters] = useState<IncidentFilters>(defaultIncidentFilters);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSourceSystemIds, setCustomerSourceSystemIds] = useState<string[] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const fetchRequestIdRef = useRef(0);
  const pendingEdgeRef = useRef<"first" | "last" | null>(null);

  useEffect(() => {
    authService.getUser().then((user: Awaited<ReturnType<typeof authService.getUser>>) => {
      const username =
        user?.profile?.preferred_username ||
        user?.profile?.name ||
        user?.profile?.email ||
        null;
      setCurrentUsername(typeof username === "string" ? username : null);
    });
  }, []);

  const fetchIncidents = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;

    if (selectedFolder === "inbox" && !currentUsername) {
      setLoading(true);
      return;
    }

    const searchFilter = buildIncidentListSearchFilter({
      filters,
      folder: selectedFolder,
      username: currentUsername,
      customerSourceSystemIds,
    });

    if (
      searchFilter === undefined &&
      filters.selectedCustomerId &&
      customerSourceSystemIds === null
    ) {
      setLoading(true);
      return;
    }

    if (searchFilter === "__NO_MATCH__") {
      if (requestId !== fetchRequestIdRef.current) return;
      setIncidents([]);
      setTotalElements(0);
      setFolderCounts((prev) => ({ ...prev, [selectedFolder]: 0 }));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await incidentService.getIncidents(
        Math.max(0, currentPage - 1),
        itemsPerPage,
        searchFilter || undefined,
        INCIDENT_LIST_FIELDS,
        `${filters.sortBy || "createdDate"},${filters.sortOrder || "desc"}`,
      );
      if (requestId !== fetchRequestIdRef.current) return;
      const page = parseIncidentsPage(data);
      setIncidents(page.content);
      setTotalElements(page.totalElements);
      setFolderCounts((prev) => ({ ...prev, [selectedFolder]: page.totalElements }));
    } catch (error) {
      console.error("Error fetching incidents:", error);
      if (requestId !== fetchRequestIdRef.current) return;
      setIncidents([]);
      setTotalElements(0);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    filters,
    customerSourceSystemIds,
    selectedFolder,
    currentUsername,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    if (!filters.selectedCustomerId) {
      setCustomerSourceSystemIds(null);
      return;
    }

    setCustomerSourceSystemIds(null);
    let cancelled = false;
    sourceSystemService
      .getSourceSystemsByCustomerId(filters.selectedCustomerId, 0, 500, undefined, ["id"])
      .then((response) => {
        if (cancelled) return;
        const ids = ((response?.content ?? []) as Array<{ id?: string }>)
          .map((item) => item.id)
          .filter((id): id is string => Boolean(id));
        setCustomerSourceSystemIds(ids);
      })
      .catch((error) => {
        console.error("Error loading customer source systems:", error);
        if (!cancelled) {
          setCustomerSourceSystemIds([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters.selectedCustomerId]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  useEffect(() => {
    if (!filters.selectedCustomerId) {
      setSelectedCustomer(null);
      return;
    }

    if (selectedCustomer?.id === filters.selectedCustomerId) {
      return;
    }

    let cancelled = false;
    customerService
      .getCustomer(filters.selectedCustomerId)
      .then((customer) => {
        if (!cancelled) {
          setSelectedCustomer(customer ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedCustomer(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters.selectedCustomerId, selectedCustomer?.id]);

  const totalPages = Math.max(1, Math.ceil(totalElements / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!pendingRestoreId || loading) return;

    const match = incidents.find((incident) => incident.id === pendingRestoreId);
    if (match) {
      setSelectedIncident(match);
      setPendingRestoreId(null);
      return;
    }

    let cancelled = false;
    incidentService
      .getIncident(pendingRestoreId)
      .then((incident) => {
        if (cancelled || !incident?.id) {
          setPendingRestoreId(null);
          return;
        }
        setSelectedIncident(incident as Incident);
        setPendingRestoreId(null);
      })
      .catch(() => {
        if (!cancelled) {
          setPendingRestoreId(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pendingRestoreId, loading, incidents]);

  useEffect(() => {
    if (pendingRestoreId) return;

    if (incidents.length === 0) {
      pendingEdgeRef.current = null;
      setSelectedIncident(null);
      return;
    }

    const edge = pendingEdgeRef.current;
    if (edge) {
      pendingEdgeRef.current = null;
      const next = edge === "last" ? incidents[incidents.length - 1] : incidents[0];
      setSelectedIncident(next);
      persistIncidentsWorkspaceSelection({
        incidentId: next.id,
        folder: selectedFolder,
      });
      return;
    }

    setSelectedIncident((current) => {
      if (!current) return incidents[0];
      const visibleMatch = incidents.find((incident) => incident.id === current.id);
      return visibleMatch ?? incidents[0];
    });
  }, [incidents, pendingRestoreId, selectedFolder]);

  const uniqueAssignees = useMemo(() => {
    const values = new Set(incidents.map((i) => i.assignedTo).filter(Boolean));
    if (filters.selectedAssignee !== "all") values.add(filters.selectedAssignee);
    return [...values];
  }, [incidents, filters.selectedAssignee]);

  const uniqueGroups = useMemo(() => {
    const values = new Set(incidents.map((i) => i.assignedGroup).filter(Boolean));
    if (filters.selectedGroup !== "all") values.add(filters.selectedGroup);
    return [...values];
  }, [incidents, filters.selectedGroup]);

  const uniqueCategories = useMemo(() => {
    const values = new Set(incidents.map((i) => i.category).filter(Boolean));
    if (filters.selectedCategory !== "all") values.add(filters.selectedCategory);
    return [...values];
  }, [incidents, filters.selectedCategory]);

  const updateFilters = useCallback((patch: Partial<IncidentFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(defaultIncidentFilters);
    setSelectedCustomer(null);
    setCustomerSourceSystemIds(null);
    setCurrentPage(1);
  }, []);

  const selectCustomer = useCallback((customer: Customer | null) => {
    setSelectedCustomer(customer);
    setCustomerSourceSystemIds(null);
    setFilters((prev) => ({
      ...prev,
      selectedCustomerId: customer?.id ?? null,
    }));
    setCurrentPage(1);
  }, []);

  const selectIncident = useCallback(
    (incident: Incident) => {
      setSelectedIncident(incident);
      persistIncidentsWorkspaceSelection({
        incidentId: incident.id,
        folder: selectedFolder,
      });
    },
    [selectedFolder],
  );

  const moveSelection = useCallback(
    (direction: 1 | -1) => {
      if (loading || incidents.length === 0 || !selectedIncident) return;
      const index = incidents.findIndex((item) => item.id === selectedIncident.id);
      if (index < 0) return;
      const nextIndex = index + direction;
      if (nextIndex >= 0 && nextIndex < incidents.length) {
        selectIncident(incidents[nextIndex]);
        return;
      }
      if (direction > 0 && currentPage < totalPages) {
        pendingEdgeRef.current = "first";
        setCurrentPage(currentPage + 1);
        return;
      }
      if (direction < 0 && currentPage > 1) {
        pendingEdgeRef.current = "last";
        setCurrentPage(currentPage - 1);
      }
    },
    [loading, incidents, selectedIncident, currentPage, totalPages, selectIncident],
  );

  const selectFolder = useCallback(
    (folderId: IncidentFolderId) => {
      pendingEdgeRef.current = null;
      setSelectedFolder(folderId);
      setCurrentPage(1);
      setSelectedIncident((current) => {
        if (current) {
          persistIncidentsWorkspaceSelection({
            incidentId: current.id,
            folder: folderId,
          });
        }
        return current;
      });
    },
    [],
  );

  return {
    incidents,
    loading,
    selectedIncident,
    setSelectedIncident: selectIncident,
    moveSelection,
    selectedFolder,
    selectFolder,
    filters,
    updateFilters,
    clearFilters,
    selectedCustomer,
    selectCustomer,
    activeFiltersCount: countActiveFilters(filters),
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalElements,
    filteredIncidents: incidents,
    paginatedIncidents: incidents,
    folderCounts,
    uniqueAssignees,
    uniqueGroups,
    uniqueCategories,
    fetchIncidents,
    currentUsername,
  };
}
